<script setup lang="ts">
/**
 * Main navigation: the desktop sidebar and the phone drawer ("More").
 * Sections: the unlabelled front door, "Work" (a plain heading, always
 * open), then You and Administration, which collapse — only one of them is
 * open at a time, so the list fits a 768px-high laptop without scrolling. The
 * section of the current page opens by itself; the last section someone
 * opened or closed is remembered per browser. `touch` makes rows 44px high.
 */
import { ChevronRight } from "@lucide/vue";
import { computed, ref, useId, watch } from "vue";
import { useRoute } from "vue-router";
import { useSession } from "@/composables/useSession";
import { cn } from "@/lib/utils";
import { activeItem, NAV, type NavSection } from "./nav";
import { usePendingApprovals } from "./usePendingApprovals";

defineProps<{ touch?: boolean }>();
defineEmits<{ navigate: [] }>();

const STORAGE_KEY = "smake.nav.open";
const collapsible = (section: NavSection) => Boolean(section.label) && section.collapsible !== false;
const DEFAULT_OPEN = "you";

const route = useRoute();
const { allows } = useSession();
// The nav renders twice while the phone "More" sheet is open: ids get a per-instance suffix.
const uid = useId();
const sectionId = (section: NavSection) => `nav-section-${section.id}-${uid}`;
const headingId = (section: NavSection) => `nav-heading-${section.id}-${uid}`;
const pendingCount = usePendingApprovals();

const sections = computed(() =>
  NAV.map((s) => ({ ...s, items: s.items.filter((i) => allows(i.requires)) })).filter((s) => s.items.length > 0),
);
const active = computed(() => activeItem(sections.value.flatMap((s) => s.items), route.path));
const activeSection = computed(
  () =>
    sections.value.find((s) => (active.value ? s.items.includes(active.value) : s.paths?.some((p) => route.path === p || route.path.startsWith(`${p}/`))))?.id ??
    null,
);

function readOpen(): string | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === null ? DEFAULT_OPEN : value || null;
  } catch {
    return DEFAULT_OPEN;
  }
}

const open = ref<string | null>(readOpen());

function toggle(id: string) {
  open.value = open.value === id ? null : id;
  try {
    localStorage.setItem(STORAGE_KEY, open.value ?? "");
  } catch {
    /* private mode: keep for this tab only */
  }
}

// The current page's section is always reachable: open it when navigating there.
watch(
  activeSection,
  (id) => {
    const section = sections.value.find((s) => s.id === id);
    if (section && collapsible(section)) open.value = section.id;
  },
  { immediate: true },
);

const isOpen = (section: NavSection) => !collapsible(section) || open.value === section.id;
</script>

<template>
  <nav aria-label="Main" class="flex flex-col gap-2.5">
    <div v-for="section in sections" :key="section.id" class="flex flex-col">
      <h2
        v-if="section.label && !collapsible(section)"
        :id="headingId(section)"
        :class="cn('flex items-center px-2.5 text-[11px] font-semibold tracking-[0.06em] text-muted-foreground uppercase', touch ? 'h-10' : 'h-7')"
      >
        {{ section.label }}
      </h2>
      <button
        v-else-if="section.label"
        type="button"
        :class="
          cn(
            'flex items-center gap-1 rounded-md px-2.5 text-[11px] font-semibold tracking-[0.06em] text-muted-foreground uppercase transition-colors duration-100 hover:bg-state-hover hover:text-foreground active:bg-state-press',
            touch ? 'h-10' : 'h-7',
          )
        "
        :aria-expanded="isOpen(section)"
        :aria-controls="sectionId(section)"
        @click="toggle(section.id)"
      >
        <span class="truncate">{{ section.label }}</span>
        <span v-if="!isOpen(section) && activeSection === section.id" class="size-1.5 rounded-full bg-primary" aria-hidden="true" />
        <ChevronRight
          :class="cn('ml-auto size-3.5 shrink-0 transition-transform duration-150', isOpen(section) && 'rotate-90')"
          aria-hidden="true"
        />
      </button>
      <ul
        v-show="isOpen(section)"
        :id="sectionId(section)"
        role="list"
        :aria-labelledby="section.label && !collapsible(section) ? headingId(section) : undefined"
        :class="cn('flex flex-col', touch && 'gap-px', section.label && 'mt-0.5')"
      >
        <li v-for="item in section.items" :key="item.to">
          <RouterLink
            :to="item.to"
            :aria-current="active === item ? 'page' : undefined"
            :class="
              cn(
                'group relative flex items-center gap-2.5 rounded-md px-2.5 font-medium transition-colors duration-100',
                touch ? 'h-11 text-[15px]' : 'h-7 text-[13px]',
                active === item
                  ? 'bg-state-selected text-primary before:absolute before:inset-y-1 before:-left-2.5 before:w-[3px] before:rounded-r-full before:bg-primary'
                  : 'text-sidebar-foreground hover:bg-state-hover hover:text-foreground active:bg-state-press',
              )
            "
            @click="$emit('navigate')"
          >
            <component
              :is="item.icon"
              :class="cn('size-4 shrink-0', active === item ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground')"
              aria-hidden="true"
            />
            <span class="truncate">{{ item.label }}</span>
            <span
              v-if="item.badge === 'approvals' && pendingCount > 0"
              class="ml-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-warning px-1.5 text-[11px] font-semibold text-black/80 tabular-nums"
              :aria-label="`${pendingCount} waiting`"
            >
              {{ pendingCount }}
            </span>
          </RouterLink>
        </li>
      </ul>
    </div>
  </nav>
</template>
